import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import { useTheme } from '../contexts/ThemeProvider';
import axiosInstance from '../utils/axiosInstance';
import './Layout.css';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [flowsExpanded, setFlowsExpanded] = useState(false);
  const [flows, setFlows] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [expandedPipelines, setExpandedPipelines] = useState({});
  const [runningFlows, setRunningFlows] = useState(new Set());
  const [runningPipelines, setRunningPipelines] = useState(new Set());

  const fetchFlowsAndPipelines = useCallback(async () => {
    if (flows.length > 0) return;
    setFlowsLoading(true);
    try {
      const [flowsRes, pipelinesRes] = await Promise.all([
        axiosInstance.get('/etl/flows/'),
        axiosInstance.get('/etl/pipelines/'),
      ]);
      setFlows(flowsRes.data);
      setPipelines(pipelinesRes.data);
    } catch (error) {
      console.error('Failed to fetch flows/pipelines:', error);
    } finally {
      setFlowsLoading(false);
    }
  }, [flows.length]);

  useEffect(() => {
    if (flowsExpanded && flows.length === 0) {
      fetchFlowsAndPipelines();
    }
  }, [flowsExpanded, flows.length, fetchFlowsAndPipelines]);

  // Group flows by pipeline
  const { pipelineGroups, ungroupedFlows } = (() => {
    const flowsInPipelines = new Set();
    const groups = pipelines.map(pipeline => {
      const pipelineFlows = (pipeline.steps || [])
        .sort((a, b) => a.step_order - b.step_order)
        .map(step => {
          const flow = flows.find(f => f.id === step.flow);
          if (flow) flowsInPipelines.add(flow.id);
          return flow ? { ...flow, layer: step.layer } : null;
        })
        .filter(Boolean);
      return { pipeline, flows: pipelineFlows };
    }).filter(g => g.flows.length > 0);

    const ungrouped = flows.filter(f => !flowsInPipelines.has(f.id));
    return { pipelineGroups: groups, ungroupedFlows: ungrouped };
  })();

  const toggleFlowsExpanded = (e) => {
    e.stopPropagation();
    setFlowsExpanded(!flowsExpanded);
  };

  const togglePipelineExpanded = (pipelineId) => {
    setExpandedPipelines(prev => ({ ...prev, [pipelineId]: !prev[pipelineId] }));
  };

  const triggerFlow = async (e, flowId) => {
    e.stopPropagation();
    if (runningFlows.has(flowId)) return;

    setRunningFlows(prev => new Set([...prev, flowId]));
    try {
      await axiosInstance.post(`/etl/flows/${flowId}/trigger/`);
      // Keep the loading state for a bit to show feedback
      setTimeout(() => {
        setRunningFlows(prev => {
          const next = new Set(prev);
          next.delete(flowId);
          return next;
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to trigger flow:', error);
      setRunningFlows(prev => {
        const next = new Set(prev);
        next.delete(flowId);
        return next;
      });
    }
  };

  const triggerPipeline = async (e, pipelineId) => {
    e.stopPropagation();
    if (runningPipelines.has(pipelineId)) return;

    // Find the flows in this pipeline
    const pipelineGroup = pipelineGroups.find(g => g.pipeline.id === pipelineId);
    const pipelineFlowIds = pipelineGroup ? pipelineGroup.flows.map(f => f.id) : [];

    // Mark pipeline and all its flows as running
    setRunningPipelines(prev => new Set([...prev, pipelineId]));
    setRunningFlows(prev => new Set([...prev, ...pipelineFlowIds]));

    try {
      await axiosInstance.post(`/etl/pipelines/${pipelineId}/trigger/`);
      // Keep the loading state for a bit to show feedback
      setTimeout(() => {
        setRunningPipelines(prev => {
          const next = new Set(prev);
          next.delete(pipelineId);
          return next;
        });
        setRunningFlows(prev => {
          const next = new Set(prev);
          pipelineFlowIds.forEach(id => next.delete(id));
          return next;
        });
      }, 3000);
    } catch (error) {
      console.error('Failed to trigger pipeline:', error);
      setRunningPipelines(prev => {
        const next = new Set(prev);
        next.delete(pipelineId);
        return next;
      });
      setRunningFlows(prev => {
        const next = new Set(prev);
        pipelineFlowIds.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const isPipelineRunning = (pipelineFlows) => {
    return pipelineFlows.some(flow => runningFlows.has(flow.id));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { 
      path: '/etl', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 4C3 3.44772 3.44772 3 4 3H16C16.5523 3 17 3.44772 17 4V6C17 6.55228 16.5523 7 16 7H4C3.44772 7 3 6.55228 3 6V4Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M3 10C3 9.44772 3.44772 9 4 9H16C16.5523 9 17 9.44772 17 10V12C17 12.5523 16.5523 13 16 13H4C3.44772 13 3 12.5523 3 12V10Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M3 16C3 15.4477 3.44772 15 4 15H16C16.5523 15 17 15.4477 17 16V18C17 18.5523 16.5523 19 16 19H4C3.44772 19 3 18.5523 3 18V16Z" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      ), 
      label: 'Dashboard', 
      description: 'Overview & Statistics' 
    },
    { 
      path: '/flows', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 5L10 1L18 5V11C18 15.4183 14.4183 19 10 19C5.58172 19 2 15.4183 2 11V5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 1V19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M2 5L10 9L18 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), 
      label: 'Flows', 
      description: 'Manage ETL Pipelines' 
    },
    { 
      path: '/runs', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M10 6V10L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ), 
      label: 'Runs', 
      description: 'Monitor Executions' 
    },
    {
      path: '/schedules',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M3 8H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M7 3V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M13 3V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="10" cy="13" r="1" fill="currentColor"/>
        </svg>
      ),
      label: 'Schedules',
      description: 'Automated Jobs'
    },
    {
      path: '/pipelines',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4H8V8H4V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 4H16V8H12V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 12H12V16H8V12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6 8V10H10V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 8V10H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      label: 'Pipelines',
      description: 'Multi-Step Workflows'
    },
    {
      path: '/run-profiles',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 5H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M4 10H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M4 15H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="7" cy="5" r="1.5" fill="currentColor"/>
          <circle cx="13" cy="10" r="1.5" fill="currentColor"/>
          <circle cx="10" cy="15" r="1.5" fill="currentColor"/>
        </svg>
      ),
      label: 'Run Profiles',
      description: 'Batch Execution Sets'
    },
    {
      path: '/lineage',
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="4" cy="10" r="2" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="16" cy="5" r="2" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="16" cy="15" r="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M6 10H10L14 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M10 10L14 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      label: 'Data Lineage',
      description: 'Flow Dependencies'
    },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo">
            <svg className="logo-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" />
            </svg>
            <span className="logo-text">Flowy</span>
          </div>
        </div>

        <nav className="nav-menu">
          {navItems.map((item) => (
            item.path === '/flows' ? (
              <div key={item.path} className="nav-item-expandable">
                <div className={`nav-item ${isActive(item.path) ? 'active' : ''}`}>
                  <button
                    className="nav-expand-btn"
                    onClick={toggleFlowsExpanded}
                    aria-label={flowsExpanded ? 'Collapse flows' : 'Expand flows'}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      className={`expand-chevron ${flowsExpanded ? 'expanded' : ''}`}
                    >
                      <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className="nav-item-main"
                    onClick={() => navigate(item.path)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <div className="nav-content">
                      <span className="nav-label">{item.label}</span>
                      <span className="nav-description">{item.description}</span>
                    </div>
                  </button>
                  {isActive(item.path) && <div className="nav-indicator"></div>}
                </div>

                {flowsExpanded && (
                  <div className="nav-submenu">
                    {flowsLoading ? (
                      <div className="nav-submenu-loading">Loading...</div>
                    ) : (
                      <>
                        {pipelineGroups.map(({ pipeline, flows: pipelineFlows }) => (
                          <div key={pipeline.id} className="nav-submenu-group">
                            <div className={`nav-submenu-header ${expandedPipelines[pipeline.id] ? 'expanded' : ''}`}>
                              <button
                                className="pipeline-expand-btn"
                                onClick={() => togglePipelineExpanded(pipeline.id)}
                              >
                                <svg
                                  width="10"
                                  height="10"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  className={`expand-chevron-small ${expandedPipelines[pipeline.id] ? 'expanded' : ''}`}
                                >
                                  <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span className="submenu-type-label">
                                  {pipeline.name}
                                </span>
                                <span className="submenu-count">{pipelineFlows.length}</span>
                              </button>
                              {runningPipelines.has(pipeline.id) || isPipelineRunning(pipelineFlows) ? (
                                <span className="pipeline-running-indicator">
                                  <svg className="spinner" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="20" strokeLinecap="round"/>
                                  </svg>
                                </span>
                              ) : (
                                <button
                                  className="pipeline-run-btn"
                                  onClick={(e) => triggerPipeline(e, pipeline.id)}
                                  title="Run pipeline"
                                >
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                    <path d="M2 1L8 5L2 9V1Z" fill="currentColor"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                            {expandedPipelines[pipeline.id] && (
                              <div className="nav-submenu-items">
                                {pipelineFlows.map(flow => (
                                  <div
                                    key={flow.id}
                                    className={`nav-submenu-item ${location.pathname === `/flows/${flow.id}` ? 'active' : ''}`}
                                  >
                                    <span className={`layer-dot ${flow.layer || 'bronze'}`} title={flow.layer === 'silver' ? 'Silver (INT)' : 'Bronze (STG)'} />
                                    <button
                                      className="flow-name-btn"
                                      onClick={() => navigate(`/flows/${flow.id}`)}
                                      title={flow.name}
                                    >
                                      {flow.name}
                                    </button>
                                    {runningFlows.has(flow.id) ? (
                                      <span className="flow-running-indicator">
                                        <svg className="spinner" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                          <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="20" strokeLinecap="round"/>
                                        </svg>
                                      </span>
                                    ) : (
                                      <button
                                        className="flow-run-btn"
                                        onClick={(e) => triggerFlow(e, flow.id)}
                                        title="Run flow"
                                      >
                                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                          <path d="M2 1L8 5L2 9V1Z" fill="currentColor"/>
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {ungroupedFlows.length > 0 && (
                          <div className="nav-submenu-group">
                            <button
                              className={`nav-submenu-header ${expandedPipelines['ungrouped'] ? 'expanded' : ''}`}
                              onClick={() => togglePipelineExpanded('ungrouped')}
                            >
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 12 12"
                                fill="none"
                                className={`expand-chevron-small ${expandedPipelines['ungrouped'] ? 'expanded' : ''}`}
                              >
                                <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span className="submenu-type-label ungrouped">
                                Ungrouped
                              </span>
                              <span className="submenu-count">{ungroupedFlows.length}</span>
                            </button>
                            {expandedPipelines['ungrouped'] && (
                              <div className="nav-submenu-items">
                                {ungroupedFlows.map(flow => (
                                  <div
                                    key={flow.id}
                                    className={`nav-submenu-item ${location.pathname === `/flows/${flow.id}` ? 'active' : ''}`}
                                  >
                                    <button
                                      className="flow-name-btn"
                                      onClick={() => navigate(`/flows/${flow.id}`)}
                                      title={flow.name}
                                    >
                                      {flow.name}
                                    </button>
                                    {runningFlows.has(flow.id) ? (
                                      <span className="flow-running-indicator">
                                        <svg className="spinner" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                          <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="20" strokeLinecap="round"/>
                                        </svg>
                                      </span>
                                    ) : (
                                      <button
                                        className="flow-run-btn"
                                        onClick={(e) => triggerFlow(e, flow.id)}
                                        title="Run flow"
                                      >
                                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                          <path d="M2 1L8 5L2 9V1Z" fill="currentColor"/>
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <button
                key={item.path}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <span className="nav-icon">{item.icon}</span>
                <div className="nav-content">
                  <span className="nav-label">{item.label}</span>
                  <span className="nav-description">{item.description}</span>
                </div>
                {isActive(item.path) && <div className="nav-indicator"></div>}
              </button>
            )
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.username || 'User'}</div>
              <div className="user-email">{user?.email || ''}</div>
            </div>
          </div>
          <button className="theme-toggle-btn" onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
            {theme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 1V3M8 13V15M15 8H13M3 8H1M13.5 2.5L12 4M4 12L2.5 13.5M13.5 13.5L12 12M4 4L2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 12C10.2091 12 12 10.2091 12 8C12 5.79086 10.2091 4 8 4C5.79086 4 4 5.79086 4 8C4 10.2091 5.79086 12 8 12Z" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 1V3M8 13V15M15 8H13M3 8H1M13.5 2.5L12 4M4 12L2.5 13.5M13.5 13.5L12 12M4 4L2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            )}
          </button>
          <button className="logout-btn" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M10 11L14 7L10 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 7H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;

